import type { AuditTrailLink, AuditTrailRow } from '@/types/audit'

/**
 * Reading an audit row as a person, not as a database row.
 *
 * Pure and dependency-free so it can be unit-tested — `vitest.config.ts` only
 * picks up `.test.ts` files under `src`, in a node environment with no DOM, so
 * anything worth asserting has to live outside the component. The table only
 * renders what these return. Same reasoning as `src/lib/storageAudit.ts`.
 */

/**
 * Who did it, as an address a reviewer can act on.
 *
 * The requirement is explicit that this column shows an EMAIL, not a user id.
 * Rows store whichever identity the writing service had — `ProductAudit`
 * stores the email, the older form trail stores a Cognito sub — and the server
 * resolves both to a user at read time, so prefer what it resolved.
 *
 * The fallbacks matter as much as the happy path. A sub that resolves to
 * nobody is a deleted account or an actor from another system, and an action
 * by somebody since removed is exactly the kind an investigator cares about:
 * show the raw stored value rather than inventing a name or hiding the row.
 */
export function auditActorLabel(row: {
  changedBy: string
  changedByActor?: { name: string | null; email: string | null } | null
}): string {
  return row.changedByActor?.email || row.changedByActor?.name || row.changedBy
}

/**
 * A JSON payload rendered compactly — `{"volumeMl":40}` reads as `volumeMl: 40`.
 *
 * The rule this exists to enforce: never show raw JSON in a trail.
 * `{"blocked":true}` is not an audit record, it is a database row on a screen.
 */
export function renderAuditValue(value: unknown): string {
  if (value == null || value === '') return '—'
  if (typeof value !== 'object') return String(value)
  if (Array.isArray(value)) {
    return value.length ? value.map(v => renderAuditValue(v)).join(', ') : '—'
  }
  const entries = Object.entries(value as Record<string, unknown>)
  if (entries.length === 0) return '—'
  return entries
    .map(([k, v]) => `${k}: ${v == null ? '—' : typeof v === 'object' ? JSON.stringify(v) : v}`)
    .join(', ')
}

/**
 * The storage position a row touched.
 *
 * The server projects it onto the row, but a row read from an older response —
 * or one this build renders before that field exists — still carries it in
 * `links`, which is where it is actually recorded. Falling back keeps the
 * column populated rather than silently empty.
 */
export function auditStorageNode(row: {
  storageNodeId?: string | null
  storageNodeLabel?: string | null
  links?: AuditTrailLink[]
}): { id: string | null; label: string | null } {
  if (row.storageNodeId) {
    return { id: row.storageNodeId, label: row.storageNodeLabel ?? null }
  }
  const link = row.links?.find(l => l.entityType === 'storage_node')
  return { id: link?.entityId ?? null, label: link?.entityLabel ?? null }
}

/**
 * Which trail a row came from, in words.
 *
 * Shown because these are one feed but not one kind of fact: "Form" is a
 * change to what the record SAYS, "Product" a change to the material it
 * describes, "Process" a change to the work being done to it. A reviewer
 * reconciling them needs to be able to tell which is which.
 */
const SOURCE_LABELS: Record<string, string> = {
  form: 'Form',
  product: 'Product',
  process: 'Process',
  equipment: 'Equipment',
  storage: 'Storage',
  inventory: 'Inventory',
  user: 'User',
  patient: 'Patient record',
}

export function auditSourceLabel(source: AuditTrailRow['source']): string {
  // An unfamiliar source from a newer server reads as itself rather than being
  // mislabelled as one of the three this build happens to know.
  return SOURCE_LABELS[source] ?? source
}

/**
 * A row's headline, for the narrow renderings that show one line.
 *
 * Prefers the system-written description — that is the sentence the column
 * exists to carry. An older row written before descriptions falls back to the
 * event name made readable, never to a blank cell.
 */
export function auditHeadline(row: AuditTrailRow): string {
  return row.description || row.eventType.replace(/_/g, ' ')
}
